// ElevenLabs Text-to-Speech Service for Spanish Voice
export class ElevenLabsListener {
  private apiKey: string;
  private baseUrl = 'https://api.elevenlabs.io/v1';
  
  // Spanish voice IDs from ElevenLabs
  private spanishVoices = {
    'maria': 'XrExE9yKIg1WjnnlVkGX', // Maria - Spanish female voice
    'diego': 'TxGEqnHWrfWFTfGW9XjX', // Diego - Spanish male voice
    'sofia': 'pNInz6obpgDQGcFmaJgB', // Sofia - Spanish female voice (alternative)
    'carlos': 'VR6AewLTigWG4xSOukaG'  // Carlos - Spanish male voice (alternative)
  };

  constructor() {
    this.apiKey = import.meta.env.VITE_ELEVENLABS_API_KEY;
    if (!this.apiKey) {
      console.warn('ElevenLabs API key not found. Voice generation will not work.');
    }
  }

  /**
   * Generate Spanish speech from text using ElevenLabs
   * @param text - The Spanish text to convert to speech
   * @param voiceName - The voice to use ('maria', 'diego', 'sofia', 'carlos')
   * @param speed - Speech speed (0.25 to 4.0, default 0.9)
   * @param stability - Voice stability (0.0 to 1.0, default 0.75)
   * @param clarity - Voice clarity (0.0 to 1.0, default 0.75)
   * @returns Promise<string | null> - Audio URL or null if failed
   */
  async generateSpanishSpeech(
    text: string, 
    voiceName: 'maria' | 'diego' | 'sofia' | 'carlos' = 'maria',
    speed: number = 0.9,
    stability: number = 0.75,
    clarity: number = 0.75
  ): Promise<string | null> {
    if (!this.apiKey) {
      console.error('ElevenLabs API key is required');
      return null;
    }

    if (!text || text.trim().length === 0) {
      console.error('Text is required for speech generation');
      return null;
    }

    try {
      const voiceId = this.spanishVoices[voiceName];
      if (!voiceId) {
        console.error(`Voice ${voiceName} not found`);
        return null;
      }

      const response = await fetch(`${this.baseUrl}/text-to-speech/${voiceId}`, {
        method: 'POST',
        headers: {
          'Accept': 'audio/mpeg',
          'Content-Type': 'application/json',
          'xi-api-key': this.apiKey
        },
        body: JSON.stringify({
          text: text.trim(),
          model_id: 'eleven_multilingual_v2', // Best model for Spanish
          voice_settings: {
            stability: Math.max(0, Math.min(1, stability)),
            similarity_boost: Math.max(0, Math.min(1, clarity)),
            style: 0.5, // Balanced style
            use_speaker_boost: true
          },
          pronunciation_dictionary_locators: [],
          seed: null,
          previous_text: null,
          next_text: null,
          previous_request_ids: [],
          next_request_ids: []
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('ElevenLabs API error:', response.status, errorText);
        
        // Handle specific error cases
        if (response.status === 401) {
          throw new Error('Invalid ElevenLabs API key');
        } else if (response.status === 429) {
          throw new Error('Rate limit exceeded. Please try again later.');
        } else if (response.status === 422) {
          throw new Error('Invalid request parameters');
        } else {
          throw new Error(`ElevenLabs API error: ${response.status}`);
        }
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      return audioUrl;
    } catch (error: any) {
      console.error('Error generating Spanish speech:', error);
      throw new Error(error.message || 'Failed to generate Spanish speech');
    }
  }

  /**
   * Generate speech for multiple text segments
   * @param textSegments - Array of Spanish text segments
   * @param voiceName - The voice to use
   * @param options - Voice generation options
   * @returns Promise<(string | null)[]> - Array of audio URLs
   */
  async generateMultipleSpanishSpeech(
    textSegments: string[],
    voiceName: 'maria' | 'diego' | 'sofia' | 'carlos' = 'maria',
    options: {
      speed?: number;
      stability?: number;
      clarity?: number;
      delayBetweenRequests?: number;
    } = {}
  ): Promise<(string | null)[]> {
    const {
      speed = 0.9,
      stability = 0.75,
      clarity = 0.75,
      delayBetweenRequests = 500 // 500ms delay between requests
    } = options;

    const results: (string | null)[] = [];

    for (let i = 0; i < textSegments.length; i++) {
      try {
        const audioUrl = await this.generateSpanishSpeech(
          textSegments[i],
          voiceName,
          speed,
          stability,
          clarity
        );
        results.push(audioUrl);

        // Add delay between requests to avoid rate limiting
        if (i < textSegments.length - 1 && delayBetweenRequests > 0) {
          await new Promise(resolve => setTimeout(resolve, delayBetweenRequests));
        }
      } catch (error) {
        console.error(`Error generating speech for segment ${i}:`, error);
        results.push(null);
      }
    }

    return results;
  }

  /**
   * Get available Spanish voices
   * @returns Object with voice information
   */
  getAvailableSpanishVoices() {
    return {
      maria: {
        id: this.spanishVoices.maria,
        name: 'María',
        gender: 'female',
        description: 'Clear, warm Spanish female voice perfect for learning',
        accent: 'Neutral Spanish'
      },
      diego: {
        id: this.spanishVoices.diego,
        name: 'Diego',
        gender: 'male',
        description: 'Professional Spanish male voice with clear pronunciation',
        accent: 'Neutral Spanish'
      },
      sofia: {
        id: this.spanishVoices.sofia,
        name: 'Sofía',
        gender: 'female',
        description: 'Expressive Spanish female voice with natural intonation',
        accent: 'Neutral Spanish'
      },
      carlos: {
        id: this.spanishVoices.carlos,
        name: 'Carlos',
        gender: 'male',
        description: 'Confident Spanish male voice ideal for educational content',
        accent: 'Neutral Spanish'
      }
    };
  }

  /**
   * Check if ElevenLabs service is available
   * @returns boolean
   */
  isAvailable(): boolean {
    return !!this.apiKey;
  }

  /**
   * Get usage information (if available)
   * @returns Promise<any> - Usage data or null
   */
  async getUsageInfo(): Promise<any> {
    if (!this.apiKey) {
      return null;
    }

    try {
      const response = await fetch(`${this.baseUrl}/user`, {
        headers: {
          'xi-api-key': this.apiKey
        }
      });

      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Error fetching usage info:', error);
    }

    return null;
  }

  /**
   * Clean up audio URLs to prevent memory leaks
   * @param audioUrls - Array of audio URLs to clean up
   */
  cleanupAudioUrls(audioUrls: (string | null)[]): void {
    audioUrls.forEach(url => {
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
  }
}

// Create a singleton instance
export const elevenLabsListener = new ElevenLabsListener();

// Export types for TypeScript
export type SpanishVoiceName = 'maria' | 'diego' | 'sofia' | 'carlos';

export interface VoiceGenerationOptions {
  speed?: number;
  stability?: number;
  clarity?: number;
  delayBetweenRequests?: number;
}

export interface SpanishVoiceInfo {
  id: string;
  name: string;
  gender: 'male' | 'female';
  description: string;
  accent: string;
}